import { FC, useMemo, useRef } from "react"
import { Fragment } from "react/jsx-runtime"
import { LibraryViewMode } from "."
import { apiFileRemove, apiFileUpdate, apiFolderRemove, FileItemProps } from "apis";
import { ContextMenuData, OnContextMenuProps, SelectedItemType } from "./";
import { AppAlert } from "components";
import FormFile from "./FormFile";
import useAppSelector from "hooks/useAppSelector";
import axios from "axios";

interface ItemFileProps {
    viewMode: LibraryViewMode;
    item: FileItemProps;
    onContextMenu: OnContextMenuProps;
    contextMenuData: ContextMenuData;
    onContextMenuClose: () => void;

    selecteds: SelectedItemType[];
    onSelect: React.Dispatch<React.SetStateAction<SelectedItemType[]>>;

    multiple?: boolean;
    allowSelect?: boolean;
    onDoubleClick?: (item: FileItemProps) => void;
}
const ItemFile: FC<ItemFileProps> = ({ viewMode, item, contextMenuData, onContextMenu, selecteds, onSelect, onContextMenuClose, multiple, allowSelect, onDoubleClick }) => {
    const user = useAppSelector(state => state.auth.user);
    const ref = useRef<HTMLUListElement>(null);
    const isSelected = useMemo(() => {
        return selecteds.findIndex(s => s.type === "file" && s.item.id === item.id) >= 0;
    }, [selecteds, item])

    const wrapperClassName = useMemo(() => {
        switch (viewMode) {
            case "grid":
                return `flex flex-col items-center p-3 border rounded hover:bg-gray-100 cursor-pointer relative ${isSelected ? " border-blue-500 bg-gray-200" : ""}`;
            case "list":
                return `flex items-center space-x-3 p-2 hover:bg-gray-50 cursor-pointer relative ${isSelected ? " bg-gray-100" : ""}`;
            default:
                return "";
        }
    }, [viewMode, isSelected])

    const labelClassName = useMemo(() => {
        switch (viewMode) {
            case "grid":
                return "mt-2 text-sm text-center truncate w-full select-none";
            case "list":
                return "truncate select-none";
            default:
                return "select-none";
        }
    }, [viewMode])

    const folderSelecteds = selecteds.filter(s => s.type === "folder");
    const fileSelecteds = selecteds.filter(s => s.type === "file");

    const getFileIcon = (mimeType?: string) => {
        if (!mimeType) return "fa-file"; // fallback

        // Nhóm icon theo loại file
        if (mimeType.startsWith("image/")) return "fa-file-image text-blue-400";
        if (mimeType.startsWith("video/")) return "fa-file-video text-purple-400";
        if (mimeType.startsWith("audio/")) return "fa-file-audio text-yellow-400";
        if (mimeType.includes("pdf")) return "fa-file-pdf text-red-500";
        if (
            mimeType.includes("msword") ||
            mimeType.includes("officedocument.wordprocessingml")
        )
            return "fa-file-word text-blue-500";
        if (
            mimeType.includes("excel") ||
            mimeType.includes("spreadsheetml")
        )
            return "fa-file-excel text-green-500";
        if (
            mimeType.includes("powerpoint") ||
            mimeType.includes("presentationml")
        )
            return "fa-file-powerpoint text-orange-500";
        if (mimeType.startsWith("text/")) return "fa-file-alt text-gray-500";
        if (mimeType.includes("zip") || mimeType.includes("rar"))
            return "fa-file-archive text-yellow-600";

        // Mặc định
        return "fa-file text-gray-500";
    };


    return <Fragment>
        <div
            onClick={(e) => {
                if (allowSelect) {
                    if (e.ctrlKey && multiple) {
                        onSelect(selecteds => {
                            if (selecteds.findIndex(s => s.type === "file" && s.item.id === item.id) >= 0) {
                                return selecteds.filter(s => s.type !== "file" || s.item.id !== item.id);
                            }
                            return [...selecteds, { item, type: "file" }];
                        });
                    }
                    else {
                        onSelect([{ item, type: "file" }]);
                    }
                }
            }}
            onContextMenu={(e) => onContextMenu(e, ref, { item, type: "file" })}
            className={wrapperClassName}
            onDoubleClick={() => {
                onDoubleClick?.(item);
            }}
        >
            <i className={`fa ${getFileIcon(item.mimeType)} text-3xl`} />
            <span className={labelClassName}>
                {item.name}
            </span>
            {/* {contextMenuData && contextMenuData.item?.type === "file" && contextMenuData.item?.item.id === item.id && ( */}
            <ul
                ref={ref}
                onContextMenu={e => e.stopPropagation()}
                onClick={(e) => {
                    e.stopPropagation();
                    onContextMenuClose();
                }}
                className="fixed bg-white border rounded shadow-lg min-w-40 w-auto z-50 max-h-[calc(100vh-10px)] overflow-y-auto"
                style={{
                    top: contextMenuData?.y ?? 0,
                    left: contextMenuData?.x ?? 0,
                    opacity: contextMenuData && contextMenuData.item?.type === "file" && contextMenuData.item?.item.id === item.id ? 1 : 0,
                    pointerEvents: contextMenuData && contextMenuData.item?.type === "file" && contextMenuData.item?.item.id === item.id ? "auto" : "none",
                }}
            >
                <li
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={(e) => {
                        const modalInstance = AppAlert.modal({
                            title: "Nhập tên tài liệu",
                            content: <div className="w-[500px]">
                                <FormFile
                                    initialValues={item}
                                    onClose={() => modalInstance.close()}
                                    onSubmit={async (values) => {
                                        const res = await apiFileUpdate({ ...item, folderId: item.folder?.id, name: values.name });
                                        if (res.status) {
                                            modalInstance.close();
                                        }
                                    }}
                                />
                            </div>
                        });
                    }}
                >
                    Đổi tên
                </li>
                {item.createdBy === user?.id && <li className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={async () => {
                        await apiFileUpdate({ ...item, folderId: item.folder?.id, public: !item.public });
                    }}
                >
                    {item.public ? "Chỉ mình tôi" : "Chia sẻ với mọi người"}
                </li>}
                <li className="px-4 py-2 hover:bg-gray-100 cursor-pointer" onClick={() => { navigator.clipboard.writeText(`${process.env.REACT_APP_FILE_URL}${item.path}`) }}>Sao chép đường dẫn file</li>
                <li
                    className="px-4 py-2 hover:bg-red-100 text-red-600 cursor-pointer aria-disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:hover:bg-transparent"
                    aria-disabled={item.createdBy !== user?.id}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (item.createdBy !== user?.id) {
                            return;
                        }
                        onContextMenuClose();
                        AppAlert.confirm({
                            title: "Bạn chắc chắn muốn xoá",
                            content: <div>
                                {
                                    fileSelecteds.findIndex(fileSelected => fileSelected.item.id === item.id) >= 0 ?
                                        <Fragment>

                                            {folderSelecteds.length > 0 && <div className="font-bold">Thư mục</div>}
                                            {folderSelecteds.map(item => <div>
                                                - {item.item.name}
                                            </div>)}
                                            {fileSelecteds.length > 0 && <div className="font-bold">Tài liệu</div>}
                                            {fileSelecteds.map(item => <div>
                                                - {item.item.name}
                                            </div>)}
                                        </Fragment> :
                                        <Fragment>
                                            <div className="font-bold">Thư mục</div>
                                            <div>- {item.name}</div>
                                        </Fragment>
                                }
                            </div>,
                            onOk: async () => {
                                if (fileSelecteds.findIndex(fileSelected => fileSelected.item.id === item.id) >= 0) {
                                    await Promise.all([
                                        folderSelecteds.length > 0 ? apiFolderRemove({ ids: folderSelecteds.map(item => item.item.id) }) : undefined,
                                        fileSelecteds.length > 0 ? apiFileRemove({ ids: fileSelecteds.map(item => item.item.id) }) : undefined
                                    ]);
                                }
                                else {
                                    await apiFileRemove({ ids: [item.id] });
                                }
                            },
                        })
                    }}
                >
                    Xóa
                </li>
            </ul>
            {/* )} */}
        </div>
    </Fragment>
}

export default ItemFile;